/**
 * useBlockingSync — Real-time Firestore listener for Blocking & Choreography
 * 
 * Subscribes to scoped project blocking and choreography subcollections
 * and pushes incoming data into the Zustand store.
 * 
 * Guards: Only subscribes when cloud.enabled, cloud.orgId, cloud.projectId, and
 * auth.initialized are all truthy — same pattern as useScheduleSync.
 */

import { useEffect, useRef } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { subscribeToBlocking, subscribeToChoreography } from '../blocking-api';
import {
    clearPendingBlockingPathWrite,
    clearPendingBlockingWrites,
    clearPendingChoreographyWrite,
    isPendingBlockingPathWrite,
    isPendingChoreographyWrite,
} from '../blocking-pending-writes';
import type { ProjectScope } from '../project-scope';
import type { BlockingPath, ChoreographySequence } from '../../blocking/types';

function normalizePendingComparisonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalizePendingComparisonValue);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([, entryValue]) => entryValue !== undefined)
                .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
                .map(([key, entryValue]) => [key, normalizePendingComparisonValue(entryValue)])
        );
    }
    return value;
}

function recordsMatchForPendingWrite(left: unknown, right: unknown): boolean {
    return JSON.stringify(normalizePendingComparisonValue(left))
        === JSON.stringify(normalizePendingComparisonValue(right));
}

function mergePendingLocalById<T extends { id: string }>(
    scope: ProjectScope,
    remoteItems: T[],
    localItems: T[],
    isPendingWrite: (scope: ProjectScope, id: string) => boolean
): T[] {
    const remoteIds = new Set(remoteItems.map(item => item.id));
    const localById = new Map(localItems.map(item => [item.id, item]));
    const mergedRemoteItems = remoteItems.map(item => {
        const localItem = localById.get(item.id);
        if (localItem && isPendingWrite(scope, item.id)) {
            return localItem;
        }
        return item;
    });
    const pendingLocalItems = localItems.filter(item => (
        !remoteIds.has(item.id) && isPendingWrite(scope, item.id)
    ));

    return [...mergedRemoteItems, ...pendingLocalItems];
}

export function mergeBlockingPathsWithPendingLocal(
    scope: ProjectScope,
    remotePaths: BlockingPath[],
    localPaths: BlockingPath[]
): BlockingPath[] {
    return mergePendingLocalById(scope, remotePaths, localPaths, isPendingBlockingPathWrite);
}

export function mergeChoreographySequencesWithPendingLocal(
    scope: ProjectScope,
    remoteSequences: ChoreographySequence[],
    localSequences: ChoreographySequence[]
): ChoreographySequence[] {
    return mergePendingLocalById(scope, remoteSequences, localSequences, isPendingChoreographyWrite);
}

export function useBlockingSync(enabled = true) {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);
    const user = useAppStore(state => state.auth.user);

    const blockingUnsubRef = useRef<(() => void) | null>(null);
    const choreoUnsubRef = useRef<(() => void) | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        // Tear down previous subscriptions on any dependency change
        if (blockingUnsubRef.current) {
            blockingUnsubRef.current();
            blockingUnsubRef.current = null;
        }
        if (choreoUnsubRef.current) {
            choreoUnsubRef.current();
            choreoUnsubRef.current = null;
        }

        // Guard: Only subscribe when all conditions are met
        if (!enabled || !cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user) {
            return;
        }

        const scope = { orgId, projectId };
        clearPendingBlockingWrites();

        // Subscribe to blocking paths
        blockingUnsubRef.current = subscribeToBlocking(scope, (paths) => {
            if (!mountedRef.current) return;
            useAppStore.setState(state => ({
                blocking: mergeBlockingPathsWithPendingLocal(scope, paths, state.blocking || []),
            }));
            const localById = new Map((useAppStore.getState().blocking || []).map(path => [path.id, path]));
            for (const path of paths) {
                const localPath = localById.get(path.id);
                if (!localPath || recordsMatchForPendingWrite(path, localPath)) {
                    clearPendingBlockingPathWrite(scope, path.id);
                }
            }
        });

        // Subscribe to choreography sequences
        choreoUnsubRef.current = subscribeToChoreography(scope, (sequences) => {
            if (!mountedRef.current) return;
            useAppStore.setState(state => ({
                choreography: mergeChoreographySequencesWithPendingLocal(scope, sequences, state.choreography || []),
            }));
            const localById = new Map((useAppStore.getState().choreography || []).map(sequence => [sequence.id, sequence]));
            for (const sequence of sequences) {
                const localSequence = localById.get(sequence.id);
                if (!localSequence || recordsMatchForPendingWrite(sequence, localSequence)) {
                    clearPendingChoreographyWrite(scope, sequence.id);
                }
            }
        });

        return () => {
            if (blockingUnsubRef.current) {
                blockingUnsubRef.current();
                blockingUnsubRef.current = null;
            }
            if (choreoUnsubRef.current) {
                choreoUnsubRef.current();
                choreoUnsubRef.current = null;
            }
        };
    }, [enabled, cloudEnabled, orgId, projectId, isAuthInitialized, user]);
}
